import _ from 'lodash';

import { LocalDate } from 'js-joda';
import { StoreType } from '@tock/shared/utils/connect';
import { appPatronProfileSelector } from 'selectors/appSelector';
import { consumer, core } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { tocktix } from '@tock/protos/dist/types/core';
import protos from 'utils/protos';

const { PatronProfile } = protos;

export const patronIsLoadingSelector = createSelector(
  (state: StoreType) => state.patron.numRequestsInProgress,
  (numRequestsInProgress: number) => numRequestsInProgress > 0
);

export const patronOpenModalSelector = (state: StoreType) => state.patron.openModal;

export const patronQuizAnswersSelector = (state: StoreType) => state.patron.quizAnswers;

export const patronQuizResultsSelector = (state: StoreType) => state.patron.quizResults;

export const patronWishlistSelector = (state: StoreType) => state.patron.wishlist;

export const getGiftCardBalance = (state: StoreType) => state.patron.tockGiftCardBalance;

export const getDoubleBookingsHash = (state: StoreType) => state.patron.purchasesAroundTime;

export const patronHasSetUpProfileSelector = createSelector(
  appPatronProfileSelector,
  (patronProfile: core.PatronProfile | undefined): boolean => {
    if (!patronProfile) {
      return false;
    }
    if (_.find(patronProfile.day, (day) => day.type !== 'BIRTHDAY')) {
      return true;
    }
    if (
      !_.isEmpty(patronProfile.patronCuisineIds) ||
      !_.isEmpty(patronProfile.patronAversions) ||
      !_.isEmpty(patronProfile.patronDietaryRestrictionIds) ||
      !_.isEmpty(patronProfile.patronHospitalityPreferenceIds)
    ) {
      return true;
    }
    if (!_.isEmpty(patronProfile.patronDestinations)) {
      return true;
    }
    return false;
  }
);

export const patronProfileImportantDaysSelector = createSelector(
  appPatronProfileSelector,
  (patronProfile: core.PatronProfile | undefined) => {
    if (patronProfile) {
      return patronProfile.day.sort(
        (a, b) =>
          localDateOfNextOccurrence(a).compareTo(localDateOfNextOccurrence(b)) ||
          deriveTypeValue(a) - deriveTypeValue(b)
      );
    } else {
      return [];
    }
  }
);

function deriveTypeValue(val: tocktix.messages.IPatronProfileDay | null | undefined) {
  return PatronProfile.DayType[val?.type ?? ''] ?? 0;
}

const TODAY = LocalDate.now();

function localDateOfNextOccurrence(profileDay: core.IPatronProfileDay) {
  // jsjoda throws an error if you pass it Feb 29 and not a leap year
  const year =
    profileDay.day === 29 && profileDay.month === 2 ? getLastLeapYear(TODAY) : TODAY.year();
  let candidate = LocalDate.of(year, profileDay.month ?? 1, profileDay.day ?? 1);
  while (candidate.isBefore(TODAY)) {
    candidate = candidate.plusYears(1);
  }
  return candidate;
}

function getLastLeapYear(date: LocalDate): number {
  if (!date.isLeapYear()) {
    return getLastLeapYear(date.minusYears(1));
  }
  return date.year();
}

export const DEFAULT_STARRED_PLAYLIST = 'Saved';
export const DEFAULT_WANT_TO_GO_PLAYLIST = 'Want to go';
export const DEFAULT_FAVORITES_PLAYLIST = 'Favorites';

export const PLAYLIST_DEFAULTS = [
  DEFAULT_STARRED_PLAYLIST,
  DEFAULT_WANT_TO_GO_PLAYLIST,
  DEFAULT_FAVORITES_PLAYLIST,
];

export const playlistsByIdSelector = createSelector(
  (state: StoreType) => state.patron.playlists.data,
  (playlistData?: consumer.Playlist[]): Dictionary<consumer.Playlist> => {
    return _.keyBy<consumer.Playlist>(playlistData || [], (playlist) => playlist.id);
  }
);
